import { Flex, IconButton, Text } from '@lego/klik-ui';
import { Trash } from '@lego/klik-ui/icons';
import type { MouseEvent } from 'react';
import { CopyableText } from '@/components/common/CopyableText';
import { createColumnHelper } from '@/components/common/table/appTable';
import { UserNameLink } from '@/components/common/UserNameLink';
import type { NetworkConfigResponse } from '@/hooks/workspace';
import { dateTimeFormat } from '@/utility/date';

const columnHelper = createColumnHelper<NetworkConfigResponse>();

export const networkConfigTableColumns = (onDelete?: (id: number) => Promise<void>) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      header: 'ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
    }),
    columnHelper.accessor('label', {
      cell: (info) => info.getValue(),
      header: 'Label',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    columnHelper.accessor('fileName', {
      cell: (info) => info.getValue(),
      header: 'Filename',
    }),
    columnHelper.accessor('interfaceName', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      header: 'Interface',
    }),
    columnHelper.accessor('connectionName', {
      cell: (info) => info.getValue(),
      header: 'Connection',
      enableSorting: false,
    }),
    columnHelper.accessor('connectionType', {
      cell: (info) => info.getValue(),
      header: 'Type',
      size: 100,
    }),
    columnHelper.accessor('digest', {
      cell: (info) => (
        <CopyableText
          copyLabel="Copy full value"
          prefixLength={8}
          suffixLength={8}
          textProps={{ fontFamily: 'mono', fontSize: 'sm' }}
          truncationMode="prefixSuffix"
          value={info.getValue()}
        />
      ),
      enableSorting: false,
      header: 'Digest',
    }),
    columnHelper.accessor('createdByOid', {
      cell: (info) => <UserNameLink oid={info.getValue()} />,
      enableSorting: false,
      header: 'Created By',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      header: 'Created At',
      meta: { filterVariant: { type: 'dateRange' } },
    }),
    columnHelper.accessor('updatedAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      header: 'Updated At',
      meta: { filterVariant: { type: 'dateRange' } },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 60,
      cell: ({ row }) => (
        <Flex gap={1}>
          {onDelete && (
            <IconButton
              aria-label="Delete network config"
              icon={<Trash />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                void onDelete(row.original.id).catch(() => {});
              }}
              size="sm"
              variant="ghost"
            />
          )}
        </Flex>
      ),
    }),
  ]);
